import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import api from '../../lib/api';
import type { Employee } from '../../types';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  Input, Button,
} from '@werkstatt/ui';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../components/ui/form';

const schema = z.object({
  first_name: z.string().min(1, 'Pflichtfeld'),
  last_name: z.string().min(1, 'Pflichtfeld'),
  employee_number: z.string().optional(),
  email: z.string().email('Ungültige E-Mail').optional().or(z.literal('')),
  phone: z.string().optional(),
  position: z.string().optional(),
  department: z.string().optional(),
  hours_per_week: z.coerce.number().min(1).max(60).default(40),
  vacation_days_per_year: z.coerce.number().min(0).max(365).default(30),
  start_date: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

interface Props {
  open: boolean;
  onClose: () => void;
  employee?: Employee | null;
}

export function EmployeeDialog({ open, onClose, employee }: Props) {
  const queryClient = useQueryClient();

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      first_name: '',
      last_name: '',
      employee_number: '',
      email: '',
      phone: '',
      position: '',
      department: '',
      hours_per_week: 40,
      vacation_days_per_year: 30,
      start_date: '',
    },
  });

  useEffect(() => {
    if (employee) {
      form.reset({
        first_name: employee.first_name,
        last_name: employee.last_name,
        employee_number: employee.employee_number || '',
        email: employee.email || '',
        phone: employee.phone || '',
        position: employee.position || '',
        department: employee.department || '',
        hours_per_week: employee.hours_per_week,
        vacation_days_per_year: employee.vacation_days_per_year,
        start_date: employee.start_date || '',
      });
    } else {
      form.reset({ first_name: '', last_name: '', hours_per_week: 40, vacation_days_per_year: 30 });
    }
  }, [employee, open, form]);

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const payload = { ...values, email: values.email || undefined, start_date: values.start_date || undefined };
      if (employee) {
        return api.put(`/employees/${employee.id}`, payload);
      }
      return api.post('/employees/', payload);
    },
    onSuccess: () => {
      toast.success(employee ? 'Mitarbeiter aktualisiert' : 'Mitarbeiter angelegt');
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      onClose();
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail || 'Fehler beim Speichern';
      toast.error(msg);
    },
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">{employee ? 'Mitarbeiter bearbeiten' : 'Mitarbeiter anlegen'}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit((v) => mutation.mutate(v))} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField control={form.control} name="first_name" render={({ field }) => (
                <FormItem>
                  <FormLabel>Vorname *</FormLabel>
                  <FormControl><Input {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={form.control} name="last_name" render={({ field }) => (
                <FormItem>
                  <FormLabel>Nachname *</FormLabel>
                  <FormControl><Input {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField control={form.control} name="employee_number" render={({ field }) => (
                <FormItem>
                  <FormLabel>Personalnummer</FormLabel>
                  <FormControl><Input {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={form.control} name="email" render={({ field }) => (
                <FormItem>
                  <FormLabel>E-Mail</FormLabel>
                  <FormControl><Input type="email" {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField control={form.control} name="phone" render={({ field }) => (
                <FormItem>
                  <FormLabel>Telefon</FormLabel>
                  <FormControl><Input {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={form.control} name="position" render={({ field }) => (
                <FormItem>
                  <FormLabel>Position</FormLabel>
                  <FormControl><Input {...field} placeholder="z.B. KFZ-Meister" /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField control={form.control} name="department" render={({ field }) => (
                <FormItem>
                  <FormLabel>Abteilung</FormLabel>
                  <FormControl><Input {...field} placeholder="z.B. Werkstatt" /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={form.control} name="start_date" render={({ field }) => (
                <FormItem>
                  <FormLabel>Eintrittsdatum</FormLabel>
                  <FormControl><Input type="date" {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField control={form.control} name="hours_per_week" render={({ field }) => (
                <FormItem>
                  <FormLabel>Soll-Stunden / Woche</FormLabel>
                  <FormControl><Input type="number" min="1" max="60" step="0.5" {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={form.control} name="vacation_days_per_year" render={({ field }) => (
                <FormItem>
                  <FormLabel>Urlaubstage / Jahr</FormLabel>
                  <FormControl><Input type="number" min="0" max="365" {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>Abbrechen</Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending ? 'Speichern...' : 'Speichern'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
